/**
 * 多级表头构建器：按表头分组生成合并区域。
 * 输入：每个顶层表头跨若干列（colspan），其下为子表头（单格）。
 */

import type { ReportIR, CellIR, DatasetIR, DatasourceIR } from '../ir/types.js';

export interface HeaderGroup {
  label: string;
  colspan: number;
}

export interface MultiHeaderInput {
  name?: string;
  datasetName: string;
  datasource: DatasourceIR;
  /** 顶层表头分组（决定列数 = 各 colspan 之和）。 */
  groups: HeaderGroup[];
  /** 子表头标签（数量需等于总列数）。 */
  subHeaders: string[];
  /** 数据字段（数量需等于总列数），绑定到数据集。 */
  fields: Array<{ name: string; type: DatasetIR['fields'][number]['type'] }>;
  title?: string;
  paper?: ReportIR['paper'];
}

export function buildMultiHeader(input: MultiHeaderInput): ReportIR {
  const { datasetName, datasource, groups, subHeaders, fields } = input;
  const columnCount = groups.reduce((s, g) => s + g.colspan, 0);
  const rowCount = 3; // 1=顶层表头, 2=子表头, 3=数据

  if (subHeaders.length !== columnCount || fields.length !== columnCount) {
    throw new Error(`多级表头列数不一致：groups 合计 ${columnCount}，子表头 ${subHeaders.length}，字段 ${fields.length}`);
  }

  const cells: CellIR[] = [];

  // 顶层表头（第1行），按 colspan 合并
  let col = 1;
  for (const g of groups) {
    cells.push({
      row: 1,
      col,
      colspan: g.colspan,
      content: { kind: 'text', value: g.label },
      style: { align: 'center', valign: 'middle', bold: true, bgcolor: '#f2f2f2' },
    });
    col += g.colspan;
  }

  // 子表头（第2行）
  subHeaders.forEach((label, i) => {
    cells.push({
      row: 2,
      col: i + 1,
      content: { kind: 'text', value: label },
      style: { align: 'center', bold: true, bgcolor: '#f9f9f9' },
    });
  });

  // 数据行（第3行，向下扩展）
  fields.forEach((f, i) => {
    cells.push({
      row: 3,
      col: i + 1,
      content: { kind: 'dataset', dataset: datasetName, field: f.name, aggregate: 'none' },
      expand: 'down',
    });
  });

  const dataset: DatasetIR = {
    name: datasetName,
    type: 'sql',
    datasource: datasource.name,
    readFieldRemark: false,
    fields: fields.map((f) => ({ name: f.name, label: f.name, type: f.type })),
  };

  return {
    name: input.name ?? '多级表头报表',
    rowCount,
    columnCount,
    cells,
    datasets: [dataset],
    datasources: [datasource],
    title: input.title ?? 'fileName()',
    paper: input.paper,
  };
}
